<form [formGroup]="form" (ngSubmit)="onSave()">
	<mat-error *ngIf="httpError === '400'" class="top-right-absolute-error-message">
		{{ 'mes.production-plan.admin.errors.bad-request' | translate }}
	</mat-error>
	<mat-error *ngIf="httpError === '500'" class="top-right-absolute-error-message">
		{{ 'mes.production-plan.admin.errors.internal-server-error' | translate }}
	</mat-error>

	<h1 mat-dialog-title class="notification-title no-select">
		{{ 'mes.production-plan.admin.edit.window-header' | translate }}
	</h1>

	<mat-dialog-content>
		<div class="optional-form-fields">
			@if (httpError === '409') {
				<mat-error>
					{{ 'mes.production-plan.admin.errors.duplicate' | translate }}
				</mat-error>
			}

			<div class="name-row">
				<div class="name-field">
					<mat-error *ngIf="controls['alias'].invalid && controls['alias'].touched">
						{{ 'mes.production-plan.admin.fields.alias.error.' + getFirstValidationErrorName('alias') | translate }}
					</mat-error>
					<mat-form-field appearance="outline" class="dialog">
						<mat-label class="mat-body-1">
							{{ 'mes.production-plan.admin.fields.alias.label' | translate }}
						</mat-label>
						<input maxlength="255" matInput formControlName="alias" />
					</mat-form-field>
				</div>
				<div class="description-field">
					<mat-error *ngIf="controls['description'].invalid && controls['description'].touched">
						{{ 'mes.production-plan.admin.fields.description.error.' + getFirstValidationErrorName('description') | translate }}
					</mat-error>
					<mat-form-field appearance="outline" class="dialog">
						<mat-label class="mat-body-1">
							{{ 'mes.production-plan.admin.fields.description.label' | translate }}
						</mat-label>
						<input maxlength="255" matInput formControlName="description" />
					</mat-form-field>
				</div>
			</div>

			<mat-form-field appearance="outline" class="dialog">
				<mat-label class="mat-body-1">
					{{ 'mes.production-plan.admin.fields.order-position-sql.label' | translate }}
				</mat-label>
				<textarea maxlength="20000" matInput formControlName="orderPositionSql" class="vertical-resize"></textarea>
			</mat-form-field>

			<!-- Info Columns -->
			<mat-label class="mat-body-1 section-label no-select">
				{{ 'mes.production-plan.admin.fields.info-columns.label' | translate }}
			</mat-label>

			<div cdkDropList [cdkDropListOrientation]="'vertical'" (cdkDropListDropped)="onDropInfoColumn($event)">
				@for (col of infoColumns.controls; track col; let i = $index) {
					<div class="drag-row" [formGroup]="col" cdkDrag cdkDragLockAxis="y">
						<mat-form-field appearance="fill" class="title-field">
							<mat-icon matPrefix cdkDragHandle class="drag-icon">reorder</mat-icon>
							<input #infoTitleInput maxlength="255" matInput formControlName="title" [placeholder]="'mes.production-plan.admin.fields.info-columns.title' | translate" />
						</mat-form-field>
						<mat-form-field appearance="fill" class="flex-fill">
							<input maxlength="255" matInput formControlName="source" [placeholder]="'mes.production-plan.admin.fields.info-columns.source' | translate" />
						</mat-form-field>
						<mat-form-field appearance="fill" class="type-field">
							<mat-select formControlName="type">
								@for (type of dataTypes; track type) {
									<mat-option [value]="type">
										{{ 'mes.production-plan.admin.fields.info-columns.types.' + type.toLowerCase() | translate }}
									</mat-option>
								}
							</mat-select>
						</mat-form-field>
						<mat-form-field appearance="fill" class="flex-fill">
							<button mat-icon-button matSuffix class="delete-button" (click)="deleteInfoColumn(i)" [disabled]="infoColumns.length <= 1" type="button">
								<mat-icon svgIcon="acies_delete"></mat-icon>
							</button>
							<input maxlength="255" matInput formControlName="format" [placeholder]="'mes.production-plan.admin.fields.info-columns.format' | translate" />
						</mat-form-field>
					</div>
				}
			</div>

			<button mat-icon-button class="add-button" (click)="addInfoColumn()" type="button">
				<mat-icon svgIcon="acies_add_alt"></mat-icon>
			</button>

			<!-- Status Columns -->
			<mat-label class="mat-body-1 section-label no-select">
				{{ 'mes.production-plan.admin.fields.status-columns.label' | translate }}
			</mat-label>

			<div cdkDropList [cdkDropListOrientation]="'vertical'" (cdkDropListDropped)="onDropStatusColumn($event)">
				@for (col of statusColumns.controls; track col; let i = $index) {
					<div class="drag-row status-row" [formGroup]="col" cdkDrag cdkDragLockAxis="y">
						<mat-form-field appearance="fill" class="title-field">
							<mat-icon matPrefix cdkDragHandle class="drag-icon">reorder</mat-icon>
							<input #statusTitleInput maxlength="255" matInput formControlName="title" [placeholder]="'mes.production-plan.admin.fields.status-columns.title' | translate" />
						</mat-form-field>
						<mat-form-field appearance="fill" class="flex-fill">
							<mat-select formControlName="terminalIds" multiple [placeholder]="'mes.production-plan.admin.fields.status-columns.terminals' | translate">
								@for (terminal of terminals; track terminal) {
									<mat-option [value]="terminal.id">({{ terminal.alias }}) {{ terminal.name }}</mat-option>
								}
							</mat-select>
						</mat-form-field>
						<mat-form-field appearance="fill" class="flex-fill">
							<button mat-icon-button matSuffix class="delete-button" (click)="deleteStatusColumn(i)" [disabled]="statusColumns.length <= 1" type="button">
								<mat-icon svgIcon="acies_delete"></mat-icon>
							</button>
							<mat-select formControlName="planLinksIds" multiple [placeholder]="'mes.production-plan.admin.fields.status-columns.plan-links' | translate">
								@for (plan of plans; track plan) {
									<mat-option [value]="plan.id">({{ plan.alias }}) {{ plan.description }}</mat-option>
								}
							</mat-select>
						</mat-form-field>
					</div>
				}
			</div>

			<button mat-icon-button class="add-button" (click)="addStatusColumn()" type="button">
				<mat-icon svgIcon="acies_add_alt"></mat-icon>
			</button>
		</div>
	</mat-dialog-content>

	<div>
		<div mat-dialog-actions align="end">
			<button mat-flat-button color="primary" type="submit" [disabled]="isLoading || form.invalid">
				{{ 'general.ok' | translate }}
			</button>
			<button mat-stroked-button type="button" (click)="onCancel()" (window:keyup.escape)="onCancel()" class="button-border-color">
				{{ 'general.cancel' | translate }}
			</button>
		</div>
	</div>
</form>
